// controllers/RegisterDictionaryController.js
// modules/RegisterDictionaryController.js
import { Modal } from '../components/modal.ts';
import { fetchRegisterDictionaries, createRegisterDictionary, updateRegisterDictionary, deleteRegisterDictionary, bulkUpsertRegisterDictionaries } from '../services/api.ts';


export class RegisterDictionaryController {
  constructor() {
    this.modal = new Modal(); // ← uses #modalRoot
    this.currentEdit = null;
  }

  async open() {
    const dicts = await fetchRegisterDictionaries();
    const html = this.renderModalHtml(dicts);
    this.modal.open(html);
    this.bindEvents(dicts);
  }

  renderModalHtml(dicts) {
    return `
      <div class="register-dict-manager">
        <div class="modal-header">
          <h3>Register Dictionary</h3>
          <button class="btn-primary btn-bulk-import">Bulk Import JSON</button>
        </div>
        <div class="modal-body">
          <div class="search-bar">
            <input type="text" placeholder="Search by name, key, group..." class="search-input" />
          </div>
          <button class="btn-primary btn-add">+ Add New</button>
          <div class="dict-table-container">
            <table class="dict-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Name</th>
                  <th>Group</th>
                  <th>Subgroup</th>
                  <th>Data Type</th>
                  <th>Unit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                ${dicts.map(d => this.renderRow(d)).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  renderRow(d) {
    return `
      <tr data-key="${d.key}">
        <td><code>${d.key}</code></td>
        <td>${d.displayName}</td>
        <td>${d.group}</td>
        <td>${d.subgroup || '-'}</td>
        <td><small>${d.defaultDataType}</small></td>
        <td><small>${d.defaultUnit || '-'}</small></td>
        <td class="actions">
          <button class="btn-edit" title="Edit">Edit</button>
          <button class="btn-delete" title="Delete">Delete</button>
        </td>
      </tr>
    `;
  }

  bindEvents(dicts) {
    const modalEl = this.modal.contentEl;

    // Search
    const searchInput = modalEl.querySelector('.search-input');
    searchInput.oninput = (e) => {
      const term = e.target.value.toLowerCase();
      modalEl.querySelectorAll('tbody tr').forEach(row => {
        const text = row.textContent.toLowerCase();
        row.style.display = text.includes(term) ? '' : 'none';
      });
    };

    modalEl.querySelector('.btn-bulk-import').onclick = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        const text = await file.text();
        try {
          const data = JSON.parse(text);
          const res = await bulkUpsertRegisterDictionaries(data);
          alert(`Bulk import complete: ${res.inserted} added, ${res.modified} updated`);
          this.open(); // refresh list
        } catch (err) {
          alert('Import failed: ' + err.message);
        }
      };
      input.click();
    };

    // Add New
    modalEl.querySelector('.btn-add').onclick = () => this.showEditForm();

    // Edit
    modalEl.querySelectorAll('.btn-edit').forEach(btn => {
      btn.onclick = () => {
        const key = btn.closest('tr').dataset.key;
        const dict = dicts.find(d => d.key === key);
        this.showEditForm(dict);
      };
    });

    // Delete
    modalEl.querySelectorAll('.btn-delete').forEach(btn => {
      btn.onclick = async () => {
        if (!confirm('Delete this dictionary entry?')) return;
        const key = btn.closest('tr').dataset.key;
        try {
          await deleteRegisterDictionary(key);
          btn.closest('tr').remove();
        } catch (err) {
          alert('Delete failed: ' + err.message);
        }
      };
    });
  }

  showEditForm(dict = null) {
    const isEdit = !!dict;
    const title = isEdit ? 'Edit Dictionary Entry' : 'Add New Dictionary Entry';

    const formHtml = `
      <div class="form-container">
        <h4>${title}</h4>
        <form id="dict-form">
          <div class="form-grid">
            <div>
              <label>Key *</label>
              <input type="text" name="key" value="${dict?.key || ''}" ${isEdit ? 'readonly' : ''} required />
            </div>
            <div>
              <label>Display Name *</label>
              <input type="text" name="displayName" value="${dict?.displayName || ''}" required />
            </div>
            <div>
              <label>Group *</label>
              <input type="text" name="group" value="${dict?.group || ''}" required />
            </div>
            <div>
              <label>Subgroup</label>
              <input type="text" name="subgroup" value="${dict?.subgroup || ''}" />
            </div>
            <div>
              <label>Default Unit</label>
              <input type="text" name="defaultUnit" value="${dict?.defaultUnit || ''}" />
            </div>
            <div>
              <label>Data Type</label>
              <select name="defaultDataType">
                ${['UINT16','INT16','UINT32','INT32','FLOAT32','BOOL','REAL'].map(dt => 
                  `<option value="${dt}" ${(!dict || dict.defaultDataType === dt) ? 'selected' : ''}>${dt}</option>`
                ).join('')}
              </select>
            </div>
            <div>
              <label>Word Order</label>
              <select name="defaultWordOrder">
                <option value="big" ${(!dict || dict.defaultWordOrder === 'big') ? 'selected' : ''}>Big Endian</option>
                <option value="little" ${dict?.defaultWordOrder === 'little' ? 'selected' : ''}>Little Endian</option>
              </select>
            </div>
            <div>
              <label>Access</label>
              <select name="defaultAccess">
                <option value="RO" ${(!dict || dict.defaultAccess === 'RO') ? 'selected' : ''}>RO</option>
                <option value="RW" ${dict?.defaultAccess === 'RW' ? 'selected' : ''}>RW</option>
                <option value="WO" ${dict?.defaultAccess === 'WO' ? 'selected' : ''}>WO</option>
              </select>
            </div>
            <div>
              <label>Register Type</label>
              <select name="defaultRegType">
                ${['HOLDING','INPUT','COIL','DISCRETE'].map(rt => 
                  `<option value="${rt}" ${(!dict || dict.defaultRegType === 'HOLDING') ? 'selected' : ''}>${rt}</option>`
                ).join('')}
              </select>
            </div>
            <div>
              <label>Scale Factor</label>
              <input type="number" step="0.001" name="defaultScaleFactor" value="${dict?.defaultScaleFactor ?? 1}" />
            </div>
            <div>
              <label>Component Type</label>
              <select name="componentType">
                ${['breaker','transformer','bus','bess','inverter','other'].map(ct => 
                  `<option value="${ct}" ${(!dict || dict.componentType === 'other') ? 'selected' : ''}>${ct}</option>`
                ).join('')}
              </select>
            </div>
            <div class="full-width">
              <label>Description</label>
              <textarea name="description">${dict?.description || ''}</textarea>
            </div>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn-primary">${isEdit ? 'Update' : 'Create'}</button>
            <button type="button" class="btn-secondary" onclick="document.querySelector('#modalRoot .modal-close').click()">Cancel</button>
          </div>
        </form>
      </div>
    `;

    this.modal.setContent(formHtml);

    const form = this.modal.contentEl.querySelector('#dict-form');
    form.onsubmit = async (e) => {
      e.preventDefault();
      const data = Object.fromEntries(new FormData(form));

      try {
        if (isEdit) {
          await updateRegisterDictionary(dict.key, data);
        } else {
          await createRegisterDictionary(data);
        }
        this.modal.close();
        this.open(); // Refresh
      } catch (err) {
        alert('Save failed: ' + err.message);
      }
    };
  }
}